/**
 * FINDINGS — the recommendation document, made readable from the site WITHOUT undoing the declutter.
 *
 * =====================================================================================
 * THE SHAPE, AND WHY IT IS THIS SHAPE
 *
 * `docs/FINDINGS.md` is ~1,600 lines. Rendering all of it into a tab would have re-created exactly
 * the wall of text the previous task deleted 2,233 lines of panel code to remove. So this panel is
 * an ANSWER, not a document viewer, in the order a reviewer needs it:
 *
 *   1. THE RECOMMENDATION, first and expanded. If a reader sees one thing, it is the answer.
 *   2. CURRENT STATE PER USE CASE — one status line each, so "what is actually running" is
 *      answerable without scrolling.
 *   3. THE LIVE CAVEATS, PROMINENT AND NEVER COLLAPSED. These are the honesty layer of the whole
 *      site. They render as warning Alerts in the open, and `scripts/verify-web-live.sh` asserts
 *      several of them in the live DOM — so softening one fails a check rather than passing quietly.
 *   4. WHAT HAS NEVER BEEN PROVEN (§6.2), also in the open, as a plain list.
 *   5. Everything else — the per-option comparisons, the capacity arithmetic, the test-result
 *      tables — is NOT here. It is named, with the document that owns it, behind one expander.
 *
 * ⚠️ WHY THE TEST RESULTS ARE NOT DUPLICATED HERE. They already have a tab, built from the parsed
 * reports, and a second copy on this tab would be a copy that can disagree with the first. A reader
 * wanting §5 is pointed at the Load test tab, which is where the measured figures live.
 *
 * ⚠️ NO FIGURE ON THIS PANEL WAS TYPED BY HAND, AND NONE WAS COPIED OUT OF THE MARKDOWN EITHER.
 * `web/findings-summary.mjs` extracts only PROSE and throws at build time on anything shaped like a
 * measurement, so the build stops rather than publishing a second, unverified copy of a number. The
 * two figures this panel does show — the reclamation flag and the block size — are read from the
 * RUNNING SERVICE's `features{}` at request time, which is the same source the UC1 panel uses. Both
 * render as an explicit "unknown" when the service does not report them, never as a default.
 *
 * The caveat text is imported from `lib/caveats.js` — the one copy — rather than restated, because
 * the way two copies of a caveat drift is that one gets softened while the other stays strict.
 * =====================================================================================
 */

import SpaceBetween from '@cloudscape-design/components/space-between';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Box from '@cloudscape-design/components/box';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import RationalePanel from './RationalePanel.jsx';
import {
  SIMULATION,
  LATENCY_CLIENT_SIDE,
  RPS_LOWER_BOUND,
  WARM_FLEET_ONLY,
  RECLAMATION_OFF,
  UC1_CONSUME_ONCE,
  UC4_R8_READ_NOT_MEASURED,
  UC4_FK3_RESIDUAL,
  C05_C06_UNREACHABLE,
  NO_FAILURE_INJECTION,
  NO_OUTAGE_DRILL,
  POOLS_UNPROVEN,
  POOLS_RATE_IS_LIFETIME_MEAN,
} from '../lib/caveats.js';
// Injected at build time by web/build.mjs from docs/FINDINGS.md. Prose only — see the guard there.
import FINDINGS from '../generated/findings.json';

/**
 * Render the {t, b, c} token array the build-time extractor emits.
 *
 * Tokens rather than markdown, deliberately: there is no runtime markdown renderer and no
 * dangerouslySetInnerHTML anywhere on this site. The deployed CSP is `script-src 'self'` with no
 * 'unsafe-inline', and adding a markdown dependency to render four sentences would be a new
 * supply-chain surface for a formatting convenience. The transform happens at build time.
 */
function Tokens({ tokens }) {
  return (
    <>
      {(tokens || []).map((tok, i) => {
        if (tok.c && tok.b) {
          return (
            <strong key={i}>
              <code>{tok.t}</code>
            </strong>
          );
        }
        if (tok.c) return <code key={i}>{tok.t}</code>;
        if (tok.b) return <strong key={i}>{tok.t}</strong>;
        return <span key={i}>{tok.t}</span>;
      })}
    </>
  );
}

export default function FindingsPanel({ metrics, live }) {
  /*
   * The only two figures on this panel, and both are LIVE READS rather than transcriptions.
   *
   * `features{}` comes from GET /v1/health on the deployed function. A service predating that field
   * omits it entirely, which renders as "unknown" — never as a misleading "off", because "we did
   * not ask" and "it is disarmed" are different claims and this page must not merge them.
   */
  const features = metrics && metrics.features && typeof metrics.features === 'object' ? metrics.features : null;
  const reclamationKnown = features && typeof features.reclamation === 'boolean';
  const blockSize = features && typeof features.blockSize === 'number' ? features.blockSize : null;

  return (
    <SpaceBetween size="l">
      {/*
        0. THE RATIONALE — WHY THESE OPTIONS AND NOT THE OTHERS.

        ⚠️ IT SITS ABOVE THE RECOMMENDATION OF RECORD, AND THE ORDERING IS THE DECISION.

        The panel below this one is the recommendation lifted verbatim out of docs/FINDINGS.md at
        build time. That is the authoritative statement of WHAT was chosen — and it says almost
        nothing about WHY, or about the five options that were not chosen. A customer asks "why that
        one, and what ruled the others out" before they ask anything else, and the answer was
        previously reachable only by reading three markdown documents totalling several thousand
        lines that are deliberately NOT published to this distribution.

        So the rationale goes FIRST, and the extracted recommendation stays immediately beneath it as
        the citation. Reversing them would put the reader in front of a conclusion whose reasoning is
        below the fold.

        ⚠️ IT IS NOT ON THE UC7 TAB, AND THAT WAS THE OTHER OPTION CONSIDERED. UC7 is the closest
        call and the richest evidence, so its panel is the tempting home — but the rationale is about
        ALL SIX options, and filing a comparison inside one option's tab makes it read as that
        option's defence. It also makes it invisible to anyone who never opens that tab.
      */}
      <RationalePanel />

      {/* ---------------------------------------------------------------- 1. the recommendation */}
      <Container
        header={
          <Header
            variant="h2"
            description="The answer this engagement reached. Everything below either qualifies it or says what it does not rest on."
            actions={<Badge color="green">recommendation</Badge>}
          >
            The recommendation
          </Header>
        }
      >
        <SpaceBetween size="m">
          <Box variant="p" fontSize="heading-s" data-testid="findings-recommendation">
            <Tokens tokens={FINDINGS.recommendation} />
          </Box>

          <Box variant="small" color="text-body-secondary">
            Extracted at build time from <Box variant="code">{FINDINGS.source}</Box>, which is the
            document of record — {FINDINGS.status} The extractor publishes PROSE ONLY and{' '}
            <strong>fails the build if a measurement appears in any sentence it lifts</strong>, so no
            figure on this page can be a retyped copy of one in that document. The measured figures
            live on the <strong>Load test</strong> tab, derived from the committed benchmark reports.
          </Box>
        </SpaceBetween>
      </Container>

      {/* ------------------------------------------------------ 2. current state per use case */}
      <Container
        header={
          <Header
            variant="h2"
            description="What is actually deployed and serving traffic today, per use case. Vend against any of these on the first three tabs."
          >
            Current state, per use case
          </Header>
        }
      >
        <SpaceBetween size="m">
          {FINDINGS.useCases.map((uc) => (
            <div key={uc.heading}>
              <Box variant="awsui-key-label">{uc.heading}</Box>
              <Box variant="p">
                <Tokens tokens={uc.status} />
              </Box>
            </div>
          ))}

          {/*
            RECLAMATION FEATURE STATE, READ FROM THE RUNNING SERVICE — not from the document.
            The document says the flag is false; this says what the deployed function actually
            reports. Those are different claims, and only the second one is evidence.
          */}
          <ColumnLayout columns={2} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Reclamation, as the live service reports it</Box>
              {reclamationKnown ? (
                <StatusIndicator
                  type={features.reclamation ? 'success' : 'stopped'}
                  data-testid="findings-reclamation-state"
                >
                  {features.reclamation ? 'ON' : 'OFF (default)'}
                </StatusIndicator>
              ) : (
                <StatusIndicator type="info" data-testid="findings-reclamation-state">
                  unknown — this service does not report feature state
                </StatusIndicator>
              )}
              <Box variant="small" color="text-body-secondary">
                {live
                  ? 'read from features{} on GET /v1/health, not from the document'
                  : 'no usable backend reachable, so nothing is claimed either way'}
              </Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Block size, as the live service reports it</Box>
              <Box>{blockSize === null ? 'n/a' : blockSize.toLocaleString('en-US')}</Box>
              <Box variant="small" color="text-body-secondary">
                {blockSize === null
                  ? 'not reported by this service, so it is not stated rather than assumed'
                  : 'read from features{} on GET /v1/health'}
              </Box>
            </div>
          </ColumnLayout>
        </SpaceBetween>
      </Container>

      {/* =====================================================================================
          3. THE LIVE CAVEATS — IN THE OPEN, NEVER BEHIND A COLLAPSED CONTROL.
          These are the honesty layer of the entire site. A caveat a reader has to click to
          discover is a caveat the reader does not see, which is the same as not having it.
          ===================================================================================== */}
      <Container
        header={
          <Header
            variant="h2"
            description="Deliberately not collapsed. Each of these is a fact about the system as it stands today, not a note about how it was built — and each qualifies a claim made elsewhere on this site."
            actions={<Badge color="red">read these</Badge>}
          >
            Live caveats
          </Header>
        }
      >
        <SpaceBetween size="m">
          <Alert type="warning" header={SIMULATION.header} data-testid="findings-caveat-simulation">
            <Box variant="p">
              {SIMULATION.midsAreFabricated} {SIMULATION.neverPrint}
            </Box>
          </Alert>

          <Alert type="warning" header="Every latency figure excludes a hop you own">
            <SpaceBetween size="xs">
              <Box variant="p" data-testid="findings-caveat-apigee">
                {LATENCY_CLIENT_SIDE}
              </Box>
              <Box variant="p">{WARM_FLEET_ONLY}</Box>
              <Box variant="p">{RPS_LOWER_BOUND}</Box>
            </SpaceBetween>
          </Alert>

          <Alert type="warning" header="Reclamation ships off and has never run against a live serial">
            <SpaceBetween size="xs">
              <Box variant="p" data-testid="findings-caveat-reclamation">
                {RECLAMATION_OFF}
              </Box>
              <Box variant="p">{UC1_CONSUME_ONCE}</Box>
            </SpaceBetween>
          </Alert>

          <Alert type="warning" header="Two things about UC4 that must not soften into a clean bill of health">
            <SpaceBetween size="xs">
              <Box variant="p" data-testid="findings-caveat-uc4-r8">
                {UC4_R8_READ_NOT_MEASURED}
              </Box>
              <Box variant="p">{UC4_FK3_RESIDUAL}</Box>
            </SpaceBetween>
          </Alert>

          <Alert type="warning" header="Failover routing is drilled; the capacity half of region failure is not">
            <SpaceBetween size="xs">
              <Box variant="p">{NO_FAILURE_INJECTION}</Box>
              <Box variant="p" data-testid="findings-caveat-no-drill">
                {NO_OUTAGE_DRILL}
              </Box>
            </SpaceBetween>
          </Alert>

          <Alert type="warning" header="What the per-pool monitoring can and cannot see">
            <SpaceBetween size="xs">
              <Box variant="p">{POOLS_UNPROVEN}</Box>
              <Box variant="p">{POOLS_RATE_IS_LIFETIME_MEAN}</Box>
            </SpaceBetween>
          </Alert>

          <Alert type="warning" header="Two constructs exist in the barcode layer but cannot be resolved">
            <Box variant="p">{C05_C06_UNREACHABLE}</Box>
          </Alert>
        </SpaceBetween>
      </Container>

      {/* ------------------------------------------------ 4. what has never been proven (§6.2) */}
      <Container
        header={
          <Header
            variant="h2"
            description="Section 6.2 of the document, in its own priority order. Also not collapsed: an unproven claim presented as settled is the one failure mode this site exists to avoid."
          >
            What has never been proven
          </Header>
        }
      >
        <SpaceBetween size="s">
          <Box variant="p" color="text-body-secondary">
            The claims below are lifted verbatim. The <em>reasoning</em> for each — what it would take
            to settle it, and the operating point it was measured at — carries figures, so it stays in
            the document rather than being retyped here.
          </Box>
          <ol data-testid="findings-unproven">
            {FINDINGS.unproven.map((u) => (
              <li key={u.n}>
                <Box variant="p">
                  <Tokens tokens={u.claim} />
                </Box>
              </li>
            ))}
          </ol>
        </SpaceBetween>
      </Container>

      {/* --------------------------------------------------------- 5. the rest of the document */}
      <Container
        header={
          <Header
            variant="h2"
            description="The deep detail is deliberately not on this page. Here is what is in the document and where each part lives."
          >
            What is in the full document
          </Header>
        }
      >
        <SpaceBetween size="m">
          <ExpandableSection headerText="The document's sections, and which of them this page summarises" variant="footer">
            <SpaceBetween size="s">
              <ul>
                {FINDINGS.sections.map((s) => (
                  <li key={s.n}>
                    <strong>
                      §{s.n}. {s.title}
                    </strong>
                    {s.n === 1 ? ' — summarised above, in full.' : null}
                    {s.n === 2 ? ' — summarised above, one status line per use case.' : null}
                    {s.n === 3
                      ? ' — the per-option comparisons and the measurement that decided each one. Not on this page.'
                      : null}
                    {s.n === 4
                      ? ' — the per-pool capacity arithmetic and the region-failure runway. Not on this page; the Capacity tab reads the live figures from GET /v1/pools.'
                      : null}
                    {s.n === 5
                      ? ' — the test-result tables. Not duplicated here: the Load test tab renders them from the committed benchmark reports, and a second copy could disagree with the first.'
                      : null}
                    {s.n === 6 ? ' — §6.2 is above; the top open item and the questions are in the document.' : null}
                  </li>
                ))}
              </ul>
              <Box variant="p">
                <Box variant="code">{FINDINGS.source}</Box> is the artifact of record and lives in the
                repository. It is <strong>deliberately not published to this site</strong>: it quotes
                named individuals at the customer, with their organisation, dates and stated traffic
                volumes, and this distribution is public. Publishing it would identify the customer far
                more precisely than anything the interface says — the same test that keeps{' '}
                <Box variant="code">outputs/all.json</Box> off this distribution. A public artifact
                carries results, never an inventory.
              </Box>
            </SpaceBetween>
          </ExpandableSection>

          <Box variant="small" color="text-body-secondary">
            This panel is generated at build time from <Box variant="code">{FINDINGS.source}</Box> by{' '}
            <Box variant="code">web/findings-summary.mjs</Box>. If the document's shape moves, the
            build FAILS rather than rendering a partial summary — the same rule the benchmark parsers
            follow, and for the same reason: a panel quietly showing less than it did is harder to
            notice than one that stops the build.
          </Box>
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
}
